<template>
  <div class="loading-state">
    <AppLoadingStatus v-if="label" :label="label" :slow-hint="slowHint" />

    <template v-if="variant === 'table'">
      <div v-for="i in count" :key="i" class="loading-row">
        <USkeleton class="h-4 w-48" />
        <USkeleton class="h-4 w-32" />
        <USkeleton class="h-4 w-24" />
        <USkeleton class="h-6 w-20" />
        <USkeleton class="h-6 w-16" />
      </div>
    </template>
    <template v-else-if="variant === 'card'">
      <div v-for="i in count" :key="i" class="console-card" style="padding:20px;">
        <USkeleton class="mb-2 h-5 w-48" />
        <USkeleton class="h-4 w-32" />
      </div>
    </template>
    <!-- KPI tiles: a grid of stat-card skeletons that wraps like the real one -->
    <template v-else-if="variant === 'kpi'">
      <div class="loading-kpis">
        <div v-for="i in count" :key="i" class="console-card kpi">
          <USkeleton class="mb-2 h-3 w-16" />
          <USkeleton class="mb-2 h-8 w-24" />
          <USkeleton class="h-3 w-20" />
        </div>
      </div>
    </template>
    <!-- Chart placeholder: bars of uneven height -->
    <template v-else-if="variant === 'chart'">
      <div class="flex items-end gap-2" :style="{ height: `${chartHeight}px` }">
        <USkeleton
          v-for="i in bars"
          :key="i"
          class="flex-1"
          :style="{ height: `${20 + ((i * 37) % 80)}%` }"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
withDefaults(defineProps<{
  variant?: 'table' | 'card' | 'kpi' | 'chart'
  count?: number
  chartHeight?: number
  bars?: number
  /** What is loading, in words. Shown with a spinner above the placeholders. */
  label?: string
  slowHint?: string
}>(), {
  variant: 'table',
  count: 10,
  chartHeight: 148,
  bars: 12,
  label: undefined,
  slowHint: undefined,
})
</script>

<style scoped>
.loading-state {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.loading-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid var(--line);
}
.loading-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(160px, 100%), 1fr));
  gap: 12px;
}
/* Nuxt UI's skeleton is a faint opacity pulse, which reads as static on the dark
   palette. A light band sweeping across each block reads as movement. */
.loading-state :deep(.animate-pulse) {
  background: linear-gradient(90deg, var(--raise) 0%, var(--line2) 50%, var(--raise) 100%);
  background-size: 200% 100%;
  animation: loading-sweep 1.3s linear infinite;
}
@keyframes loading-sweep {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .loading-state :deep(.animate-pulse) {
    background: var(--raise);
    animation: none;
  }
}
</style>
